import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Printer, FileText, Loader2, Eye, FileType } from "lucide-react";
import { getReportConfig } from "../utils/report-config";
import { FleetReportData } from "@/types/fleet-reports";
import { ConsolidatedExecutivePreview } from "./ConsolidatedExecutivePreview";

interface ReportContentProps {
  reportType: string;
  filters: any;
  hasData: boolean;
  pdfUrl: string | null;
  onDownload: () => void;
  onPrint: () => void;
  isGenerating?: boolean;
  reportData?: FleetReportData | null;
}

export function ReportContent({ 
  reportType, 
  filters, 
  hasData,
  pdfUrl,
  onDownload,
  onPrint,
  isGenerating,
  reportData
}: ReportContentProps) {
  const [viewMode, setViewMode] = useState<"preview" | "pdf">("preview");
  const config = getReportConfig(reportType);

  // Estado 1: Nenhum relatório selecionado
  if (!reportType) {
    return (
      <Card className="h-full flex items-center justify-center">
        <CardContent className="text-center py-12">
          <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Selecione um tipo de relatório</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Escolha o tipo de relatório que deseja gerar no menu lateral e aplique os filtros desejados.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!config) {
    return (
      <Card className="h-full flex items-center justify-center">
        <CardContent className="text-center py-12">
          <p className="text-muted-foreground">Tipo de relatório não encontrado</p>
        </CardContent>
      </Card>
    );
  }

  // Estado 2: Gerando PDF (loading)
  if (isGenerating) {
    return (
      <Card className="h-full flex items-center justify-center">
        <CardContent className="text-center py-12">
          <Loader2 className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium mb-2">Gerando relatório...</h3>
          <p className="text-sm text-muted-foreground">
            Processando dados e criando PDF
          </p>
        </CardContent>
      </Card>
    );
  }

  // Estado 3: Dados disponíveis - mostrar preview ou PDF
  if (hasData && reportData) {
    // Para relatório consolidado, usar o preview executivo
    const isConsolidated = reportType === "consolidated";
    
    return (
      <Card className="h-full flex flex-col">
        {/* Header com título e ações */}
        <div className="p-4 border-b flex items-center justify-between bg-muted/30">
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              Relatório de {config.label}
            </h2>
            <p className="text-sm text-muted-foreground">
              Gerado em {new Date().toLocaleString('pt-BR')}
            </p>
          </div>
          
          <div className="flex gap-2">
            {/* Toggle de visualização apenas para consolidated */}
            {isConsolidated && pdfUrl && (
              <div className="flex border rounded-lg overflow-hidden mr-2">
                <Button 
                  variant={viewMode === "preview" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("preview")}
                  className="rounded-none"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Preview
                </Button>
                <Button 
                  variant={viewMode === "pdf" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("pdf")}
                  className="rounded-none"
                >
                  <FileType className="h-4 w-4 mr-1" />
                  PDF
                </Button>
              </div>
            )}
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={onPrint}
              disabled={!pdfUrl}
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
            <Button 
              variant="default" 
              size="sm"
              onClick={onDownload}
              disabled={!pdfUrl}
            >
              <Download className="h-4 w-4 mr-2" />
              Salvar PDF
            </Button>
          </div>
        </div>

        {/* Conteúdo: Preview HTML ou PDF */}
        <div className="flex-1 relative min-h-0 overflow-auto">
          {isConsolidated && viewMode === "preview" ? (
            <div className="p-6">
              <ConsolidatedExecutivePreview 
                data={reportData} 
                onExportPdf={onDownload}
              />
            </div>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title="Relatório PDF"
              style={{ minHeight: '600px' }}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">PDF sendo preparado...</p>
            </div>
          )}
        </div>
      </Card>
    );
  }

  // Estado 4: PDF gerado mas sem reportData (fallback para outros tipos)
  if (pdfUrl) {
    return (
      <Card className="h-full flex flex-col">
        {/* Header com título e ações */}
        <div className="p-4 border-b flex items-center justify-between bg-muted/30">
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              Relatório de {config.label}
            </h2>
            <p className="text-sm text-muted-foreground">
              Gerado em {new Date().toLocaleString('pt-BR')}
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={onPrint}
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimir
            </Button>
            <Button 
              variant="default" 
              size="sm"
              onClick={onDownload}
            >
              <Download className="h-4 w-4 mr-2" />
              Salvar PDF
            </Button>
          </div>
        </div>

        {/* PDF Viewer - ocupa todo o espaço restante */}
        <div className="flex-1 relative min-h-0">
          <iframe
            src={pdfUrl}
            className="w-full h-full border-0"
            title="Relatório PDF"
            style={{ minHeight: '600px' }}
          />
        </div>
      </Card>
    );
  }

  // Estado 5: Aguardando geração
  return (
    <Card className="h-full flex items-center justify-center">
      <CardContent className="text-center py-12">
        <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium mb-2">
          Pronto para gerar
        </h3>
        <p className="text-sm text-muted-foreground max-w-md">
          Configure os filtros desejados e clique em "Gerar Relatório" para visualizar o PDF
        </p>
      </CardContent>
    </Card>
  );
}
